/**
 * UPT-2096 (P1-a, §4.1, §6.0) — brouillon du dialogue d'import → partie
 * `metadata` (forme EXACTE de PrototypeImportInput : aucune clé en plus,
 * jamais makerOrganisation) et blocages du bouton « Importer ». Fonctions
 * pures.
 */
import {
  PROTOTYPE_LIMITS,
  PROTOTYPE_RIGHTS_TEXT_VERSIONS,
  PROTOTYPE_SOURCE_LIMITS,
  type PrototypeClientWarningInput,
  type PrototypeDerivationIntent,
  type PrototypeImportInput,
  type PrototypeToolName,
} from '../data/contracts';
import type { PreparedHtml, PreparedImage, PreparedMarkdown } from './files';
import { resizedExample, utf8ByteLength } from './files';

export interface ImportDraft {
  title: string;
  /** Outil qui a fabriqué la maquette (traçabilité seulement) ; facultatif : `null` part comme `other`, jamais un blocage de l'import. */
  toolName: PrototypeToolName | null;
  toolDetail: string;
  promptText: string;
  /** L2 : toutes les sources HTML dans l'ordre (rang 0 = source principale). */
  htmls: PreparedHtml[];
  /** L2 : documents Markdown (rôle `prompt` ou `notes`). */
  markdowns: PreparedMarkdown[];
  images: PreparedImage[];
  derivedFrom: string[];
  derivation: PrototypeDerivationIntent;
  attestationNoRealData: boolean;
  attestationRights: boolean;
  supersedesImportId: string | null;
}

/** Métadonnées fournies explicitement par le Maker ; le serveur signe avec son acteur authentifié. */
export type ImportCommandParameters = Pick<ImportDraft, 'promptText' | 'attestationNoRealData' | 'attestationRights' | 'derivedFrom' | 'derivation'>
  & Partial<Pick<ImportDraft, 'title' | 'toolName' | 'toolDetail'>> & { reviewed: boolean };

export function importMetadataReviewed(parameters?: Partial<ImportCommandParameters>): boolean {
  return parameters?.reviewed === true && typeof parameters.promptText === 'string'
    && parameters.attestationNoRealData === true && parameters.attestationRights === true
    && Array.isArray(parameters.derivedFrom) && parameters.derivation !== undefined;
}

/**
 * Prompt normalisé (comme rest §4.7.2) : retrait des caractères de contrôle
 * sauf tabulation, CR, LF ; CRLF et CR → LF ; aucune autre modification,
 * jamais de troncature.
 */
export const normalizePromptText = (raw: string): string => {
  let out = '';
  for (let i = 0; i < raw.length; i += 1) {
    const char = raw[i];
    if (char === '\r') {
      if (raw[i + 1] === '\n') continue;
      out += '\n';
    } else if (char === '\t' || char === '\n' || !/\p{Cc}/u.test(char)) {
      out += char;
    }
  }
  return out;
};

/**
 * Longueur du prompt que le serveur retiendra (rest §4.4.2) : le texte saisi à l'étape 2 s'il n'est pas blanc, sinon celui du
 * document de rôle `prompt` (le serveur lit le fichier quand la zone est vide). 0 sans prompt.
 */
export const effectivePromptLength = (draft: Pick<ImportDraft, 'promptText' | 'markdowns'>): number => {
  const typed = normalizePromptText(draft.promptText);
  if (typed.trim() !== '') return typed.length;
  const file = draft.markdowns.find((document) => document.role === 'prompt');
  const fromFile = file ? normalizePromptText(file.text) : '';
  return fromFile.trim() === '' ? 0 : fromFile.length;
};

/** Source principale (rang 0) d'un brouillon (accesseur de compatibilité P1-a). */
export const draftHtml = (draft: Pick<ImportDraft, 'htmls'>): PreparedHtml | null => draft.htmls[0] ?? null;

export type ImportBlocker =
  | 'title'
  | 'files'
  | 'prompt-too-long'
  | 'attestation-no-real-data'
  | 'attestation-rights'
  | 'metadata-too-large'
  | 'sources-order'
  | 'prompt-role';

/** Manques qui bloquent « Importer » ; la raison est affichée à côté du bouton. */
export const importBlockers = (draft: ImportDraft): ImportBlocker[] => {
  const blockers: ImportBlocker[] = [];
  if (!draft.title.trim()) blockers.push('title');
  if (draft.htmls.length === 0 && draft.images.length === 0) blockers.push('files');
  if (draft.markdowns.filter((doc) => doc.role === 'prompt').length > 1) blockers.push('prompt-role');
  if (draft.htmls.length > PROTOTYPE_SOURCE_LIMITS.maxHtml) blockers.push('sources-order');
  if (effectivePromptLength(draft) > PROTOTYPE_LIMITS.promptMaxChars) {
    blockers.push('prompt-too-long');
  }
  if (!draft.attestationNoRealData) blockers.push('attestation-no-real-data');
  if (!draft.attestationRights) blockers.push('attestation-rights');
  if (utf8ByteLength(JSON.stringify(buildPrototypeImportInput(draft))) > PROTOTYPE_LIMITS.metadataMaxBytes) {
    blockers.push('metadata-too-large');
  }
  return blockers;
};

/**
 * Partie `metadata` : prompt blanc → null ; toolDetail vide → null ;
 * imageCount = nombre d'images ; rightsTextVersion = version la plus récente ;
 * htmlCount = nombre d'HTML, documents = rôles dans l'ordre des .md (L2) ;
 * clientWarnings de la partie html (source 0) et de la première image SEULEMENT (les
 * images suivantes partent avec leur propre envoi) ; jamais
 * makerOrganisation.
 */
export const buildPrototypeImportInput = (draft: ImportDraft): PrototypeImportInput => {
  const prompt = normalizePromptText(draft.promptText);
  const toolDetail = draft.toolDetail.trim();
  const clientWarnings: PrototypeClientWarningInput[] = [];
  const mainHtml = draftHtml(draft);
  if (mainHtml && mainHtml.fallback) {
    clientWarnings.push({ code: 'html.encoding_fallback', count: 1, example: null, part: 'html' });
  }
  const firstImage = draft.images[0] ?? null;
  if (firstImage && (firstImage.width !== firstImage.sourceWidth || firstImage.height !== firstImage.sourceHeight)) {
    clientWarnings.push({
      code: 'image.resized',
      count: 1,
      example: resizedExample(
        { width: firstImage.sourceWidth, height: firstImage.sourceHeight },
        { width: firstImage.width, height: firstImage.height }
      ),
      part: 'image',
    });
  }
  return {
    title: draft.title.trim(),
    toolName: draft.toolName ?? 'other',
    toolDetail: toolDetail === '' ? null : toolDetail,
    promptText: prompt.trim() === '' ? null : prompt,
    htmlCount: draft.htmls.length,
    documents: draft.markdowns.map(({ role }) => ({ role })),
    imageCount: draft.images.length,
    derivedFrom: draft.derivedFrom.map((moduleCode) => ({ moduleCode })),
    derivation: draft.derivation,
    attestationNoRealData: draft.attestationNoRealData as true,
    attestationRights: draft.attestationRights as true,
    rightsTextVersion: PROTOTYPE_RIGHTS_TEXT_VERSIONS[PROTOTYPE_RIGHTS_TEXT_VERSIONS.length - 1],
    supersedesImportId: draft.supersedesImportId,
    clientWarnings,
  };
};
